/**
 * Word Wagon — what falls, where and when (specs/wagon-mode.md §7). Phaser-free, so the one rule
 * this mode cannot get wrong is testable in node.
 *
 * The rule, stated once:
 *
 *   > **The board may make you drop a word. It may never make you eat one.**
 *
 * Every hazard must be avoidable at every rung, because a hazard wrecks a wagon and being
 * punished for an impossible situation is what makes a child stop trusting a game. Targets are
 * different: from `sacrificeFrom` the schedule may put two of them where only one can be reached,
 * and that is fair precisely because dropping one costs a crate, which is recoverable.
 *
 * Neither half can be argued from the spawn rate — two hazards 300 ms apart in lanes 1 and 7 are
 * individually harmless and jointly impossible — so both are *simulated* over the generated
 * schedule, and a schedule that fails is re-rolled (§7.1).
 */
import { WAGON } from '@/game/config';
import { loadFor, spawnMsFor, fallMsFor, targetRatioFor, lanesFor, type WagonWord } from './track';

/** One word released by the freighter. */
export interface Drop {
  /** ms from the start of this wagon, when the freighter lets it go. */
  spawnAt: number;
  /**
   * ms from the start of this wagon, when it reaches the rail — `spawnAt + fallMs`.
   *
   * This is the EMPTY-lane landing, which is exactly what a hazard always does (it falls the whole
   * way and drops through the grate). A missed target may land earlier, on its lane's pile — see
   * `track.pileFraction`, and note that the pile can therefore take a target away but can never
   * move a hazard, which is what keeps the guarantee above exact.
   */
  landAt: number;
  lane: number;
  word: WagonWord;
}

export interface ScheduleOptions {
  round: number;
  /** Words of the class on the sign. Must not be empty. */
  targets: readonly WagonWord[];
  /** Words that are not, and that `canBeDecoy` has cleared (§6.2). Must not be empty. */
  hazards: readonly WagonWord[];
  /** Lane the wagon is standing in when this batch starts. */
  startLane: number;
  /** Drops to schedule. Defaults to three times the wagon's capacity — enough targets to fill it
   *  even after a few misses, and short enough that a re-roll is cheap. */
  count?: number;
  rnd?: () => number;
}

/** What a simulated wagon could do with a schedule, played perfectly. */
export interface Walk {
  /** False when some hazard cannot be dodged at all — the schedule is unfair and must be re-rolled. */
  safe: boolean;
  /** Targets a perfect player could catch while dodging every hazard. */
  maxCatchable: number;
  /** Targets in the schedule. */
  targets: number;
}

const NEVER = -1;

/**
 * The exact answer to "what could a perfect player do here", by walking the whole schedule with a
 * best-count-per-lane table rather than a single position.
 *
 * A single greedy position cannot answer this: taking a target now can be what makes the next one
 * unreachable, and the wagon is allowed to stand still and let one fall. With at most seven lanes
 * the whole table is seven numbers, so the honest version is also the cheap one.
 */
export function walkSchedule(
  drops: readonly Drop[],
  lanes: number,
  startLane: number,
): Walk {
  const events = [...drops].sort((a, b) => a.landAt - b.landAt);
  let best: number[] = Array.from({ length: lanes }, (_, l) => (l === startLane ? 0 : NEVER));
  let last = 0;
  let targets = 0;

  for (const d of events) {
    const steps = Math.floor((d.landAt - last) / WAGON.laneMs);
    last = d.landAt;

    // Drive: every lane inherits the best count within reach of it.
    const moved = best.map((_, l) => {
      let top = NEVER;
      for (let p = Math.max(0, l - steps); p <= Math.min(lanes - 1, l + steps); p++) {
        if (best[p] > top) top = best[p];
      }
      return top;
    });
    best = moved;

    if (d.word.target) {
      targets++;
      // Standing under it catches it; standing anywhere else is still allowed (a sacrifice).
      if (best[d.lane] !== NEVER) best[d.lane] += 1;
    } else {
      // A hazard: that lane is simply not a place the wagon may be at this instant.
      best[d.lane] = NEVER;
      if (best.every((v) => v === NEVER)) {
        return { safe: false, maxCatchable: 0, targets };
      }
    }
  }

  return { safe: true, maxCatchable: Math.max(...best, 0), targets };
}

/**
 * The trap the table above cannot see, because it assumes a perfect player and this is about a
 * good one: a hazard landing in the lane a target landed in a moment earlier. The child did the
 * right thing, is standing where the right thing was, and cannot get out in time.
 *
 * Half a lane's travel is the bar (`laneMs`): less than that and leaving is not a decision, it is
 * a reflex nobody has.
 */
export function hasHazardTrap(drops: readonly Drop[]): boolean {
  for (const h of drops) {
    if (h.word.target) continue;
    for (const t of drops) {
      if (!t.word.target || t.lane !== h.lane) continue;
      const gap = h.landAt - t.landAt;
      if (gap >= 0 && gap < WAGON.laneMs) return true;
    }
  }
  return false;
}

/** Fisher-Yates, so a bag of lanes is dealt evenly rather than sampled with `rnd() * lanes`. */
function shuffled<T>(items: readonly T[], rnd: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * One batch of drops for `round`.
 *
 * Three separate things are random here and each has its own classic bug (§7.4): which words fall
 * (a stable `slice` deals the same eight words in every run), which lane each uses (an unbagged
 * `rnd() * lanes` leaves the outer lanes visibly under-used, so the wagon never has to travel), and
 * the kind order (a fixed alternation is learnable without reading a thing).
 *
 * Returns the batch only once it is walkable. Every re-roll after the first also widens the
 * spacing a little: a schedule that keeps failing is usually one asking for two places at once, and
 * time is the only thing that fixes that.
 */
export function buildSchedule(opts: ScheduleOptions): Drop[] {
  const rnd = opts.rnd ?? Math.random;
  const { round, startLane } = opts;
  const lanes = lanesFor(round);
  const capacity = loadFor(round);
  const count = opts.count ?? capacity * 3;
  const fall = fallMsFor(round);
  const requireEveryTarget = round < WAGON.sacrificeFrom;

  for (let attempt = 0; attempt < WAGON.dealAttempts; attempt++) {
    // Widen from halfway through the attempts, not from the first retry: a re-roll is usually
    // enough on its own, and stretching early would quietly slow a rung the ladder set.
    const stretch = attempt < WAGON.dealAttempts / 2 ? 1 : 1 + (attempt - WAGON.dealAttempts / 2) * 0.12;
    const drops = deal(opts, rnd, count, stretch);
    if (hasHazardTrap(drops)) continue;
    const walk = walkSchedule(drops, lanes, startLane);
    if (!walk.safe) continue;
    // Below sacrificeFrom every target must be catchable, so a perfect wagon is always available
    // and the refund of §5.5 is always earnable. Above it, only one thing still has to hold: the
    // wagon must remain fillable, or a run could stall on a batch nobody can complete.
    if (requireEveryTarget ? walk.maxCatchable === walk.targets : walk.maxCatchable >= capacity) {
      return drops;
    }
  }

  // Every attempt failed: fall back to a schedule that cannot be unfair, one word at a time with a
  // full fall between them. Slower than the rung asks for and unreachable in practice — but a
  // playable board is the one thing this function may never fail to return.
  return deal(opts, rnd, count, (fall + WAGON.laneMs * lanes) / spawnMsFor(round));
}

/** One un-checked candidate batch. */
function deal(
  opts: ScheduleOptions,
  rnd: () => number,
  count: number,
  stretch: number,
): Drop[] {
  const { round, targets, hazards } = opts;
  const lanes = lanesFor(round);
  const spawn = spawnMsFor(round) * stretch;
  const fall = fallMsFor(round);
  const capacity = loadFor(round);

  // Kinds first: enough targets to fill the wagon whatever the ratio says, then shuffled so the
  // sequence is not readable as a pattern.
  const wanted = Math.max(capacity, Math.round(count * targetRatioFor(round)));
  const kinds = shuffled(
    Array.from({ length: count }, (_, i) => i < wanted),
    rnd,
  );

  const laneBag: number[] = [];
  const nextLane = (): number => {
    if (laneBag.length === 0) laneBag.push(...shuffled(Array.from({ length: lanes }, (_, i) => i), rnd));
    return laneBag.pop() as number;
  };

  const recent: string[] = [];   // ids in the air / just gone, so a word never doubles up
  const pick = (pool: readonly WagonWord[]): WagonWord => {
    const fresh = pool.filter((w) => !recent.includes(w.id));
    const from = fresh.length > 0 ? fresh : pool;
    const w = from[Math.floor(rnd() * from.length)];
    recent.push(w.id);
    if (recent.length > 4) recent.shift();
    return w;
  };

  const drops: Drop[] = [];
  let t = 0;
  for (const isTarget of kinds) {
    t += spawn * (0.85 + rnd() * 0.3);

    // A lane is only usable if nothing else is falling in it too close behind: two words in one
    // lane arrive as one object, and the wagon is either there or not (§7.3).
    let lane = nextLane();
    for (let tries = 0; tries < lanes; tries++) {
      const clash = drops.some((d) => d.lane === lane && Math.abs(d.spawnAt - t) < WAGON.minSameLaneGapMs);
      if (!clash) break;
      lane = nextLane();
    }

    const pool = isTarget ? targets : hazards;
    if (pool.length === 0) continue;
    drops.push({ spawnAt: t, landAt: t + fall, lane, word: pick(pool) });
  }
  return drops;
}
